// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 26-versioning-update-strategy.md
// @greenlight-spec: 19-design-system.md

import type { StatusBadgeVariant } from '@/components/greenlight/StatusBadge';
import {
  isPostApplyCheckFailure,
  POST_APPLY_CHECK_FAILED_REASON,
  type UpdateRunPhase,
  type UpdateRunSummary,
} from '@greenlight/shared';

export interface RunOutcomeMeta {
  variant: StatusBadgeVariant;
  label: string;
  /** One sentence naming what the outcome means for the install. */
  meaning: string;
}

/** The check that did not pass, from the reason's `post_apply_check_failed:<check>` tail. */
function postApplyCheckName(reason: string | null): string | null {
  if (reason === null || !reason.startsWith(`${POST_APPLY_CHECK_FAILED_REASON}:`)) return null;
  return reason.slice(POST_APPLY_CHECK_FAILED_REASON.length + 1) || null;
}

const RUN_IN_PROGRESS = 'run_in_progress';

/** The Job that held the Lease, from a skipped run's `run_in_progress:<job>` reason. */
function leaseHolder(reason: string | null): string | null {
  if (reason === null || !reason.startsWith(`${RUN_IN_PROGRESS}:`)) return null;
  return reason.slice(RUN_IN_PROGRESS.length + 1) || null;
}

/**
 * Codes whose own words do not survive the underscore strip. `feed_unchanged`
 * names the conditional request the release feed answered `304` to; what IT
 * needs from it is that there was nothing to take.
 */
const REASON_WORDS = new Map<string, string>([['feed_unchanged', 'no new releases']]);

/** Bounded reason codes are snake_case; render them as words, not identifiers. */
export const reasonLabel = (reason: string): string =>
  REASON_WORDS.get(reason) ?? reason.replace(/_/g, ' ');

/**
 * The run mode in the words the card offers it under: `canary` is the wire
 * value, *preview* the only word a user sees for it.
 */
export const modeLabel = (mode: UpdateRunSummary['mode']): string =>
  mode === 'canary' ? 'preview' : mode;

/**
 * How one run reads on the Runs list and in its detail. A clean apply, an
 * apply whose closing check failed, a run that never reported, and a skipped
 * run each take their own words; the last two share the neutral colour, since
 * neither is a failure.
 */
export function runOutcomeMeta(run: Pick<UpdateRunSummary, 'outcome' | 'reason'>): RunOutcomeMeta {
  if (isPostApplyCheckFailure(run)) {
    const check = postApplyCheckName(run.reason);
    return {
      variant: 'degraded',
      label: 'Applied · closing check failed',
      meaning: check
        ? `The release applied and is running; the installer's closing ${check} check did not pass.`
        : "The release applied and is running; one of the installer's closing checks did not pass.",
    };
  }

  switch (run.outcome) {
    case 'applied':
      return {
        variant: 'active',
        label: 'Applied',
        meaning: 'The release applied and the smoke test confirmed it.',
      };
    case 'up_to_date':
      return {
        variant: 'active',
        label: 'Up to date',
        meaning: 'Nothing newer was eligible, so the run had nothing to apply.',
      };
    case 'canary_plan_only':
      return {
        variant: 'active',
        label: 'Preview · nothing applied',
        meaning:
          'A preview discovers, verifies, and plans the release without applying it. Its transcript is the plan-only adoption proof.',
      };
    case 'failed':
      return {
        variant: 'failed',
        label: 'Failed',
        meaning: 'The run stopped before finishing and left the installation unchanged.',
      };
    case 'unreported': {
      const why = run.reason !== null ? reasonLabel(run.reason) : null;
      return {
        variant: 'inactive',
        label: why !== null ? `Never reported · ${why}` : 'Never reported',
        meaning:
          why !== null
            ? `The Job finished without recording a run (${why}). Nothing decided this run failed.`
            : 'The Job finished without recording a run, so what it did is known only from the Job’s own conditions. Nothing decided this run failed.',
      };
    }
    case 'skipped': {
      const holder = leaseHolder(run.reason);
      return {
        variant: 'inactive',
        label:
          holder !== null ? `Skipped · waited for ${holder}` : 'Skipped · another run was going',
        meaning: `${holder !== null ? `Another updater run (${holder})` : 'Another updater run'} held the run lease for the whole wait, so this run did nothing. Nothing went wrong; the next run proceeds once that one finishes.`,
      };
    }
    case 'deferred_validation_in_flight':
      return {
        variant: 'inactive',
        label: 'Deferred · validation running',
        meaning:
          'A release-validation run was still in flight for the installed build, so this run applied nothing. The next run proceeds once it finishes or times out.',
      };
    case null:
      return {
        variant: 'deploying',
        label: 'Running',
        meaning:
          'The run is still going. Its phases appear as it records them; the transcript is stored when it finishes.',
      };
    default:
      return {
        variant: 'inactive',
        label: reasonLabel(run.outcome),
        meaning:
          'The install reported an outcome this dashboard does not know, shown as it was sent.',
      };
  }
}

/**
 * Why the transcript section is empty. The outcome line already says a running
 * run stores its transcript when it finishes, so this does not say that again.
 */
export function emptyTranscriptCopy(outcome: UpdateRunSummary['outcome']): string {
  switch (outcome) {
    case 'up_to_date':
      return 'No transcript was stored for this run. An up-to-date check stores none — its run record is the whole story.';
    case 'unreported':
      return 'No transcript was stored for this run. A run that could not reach the database leaves none.';
    case 'skipped':
      return 'No transcript was stored for this run. A run that waited on another stores none, since it did nothing to record.';
    case 'deferred_validation_in_flight':
      return 'No transcript was stored for this run. A run that deferred to a validation in flight did nothing to record.';
    case null:
      return 'No transcript has been stored yet.';
    default:
      return 'No transcript was stored for this run.';
  }
}

/** `smoke_test` → `smoke test` — phase and result codes are snake_case in the ledger. */
export const phaseLabel = (phase: string): string => phase.replace(/_/g, ' ');

/** Ledger phase status as the same operator words the run outcome already uses. */
export function phaseStatusLabel(status: UpdateRunPhase['status']): string {
  switch (status) {
    case 'succeeded':
      return 'Succeeded';
    case 'failed':
      return 'Failed';
    case 'started':
      return 'Still running';
    case 'skipped':
      return 'Skipped';
    default:
      return reasonLabel(status);
  }
}

/** A duration in ms as the coarse figure a run list needs: "2 s", "14 min". */
export function durationLabel(ms: number | null): string | null {
  if (ms === null) return null;
  if (ms < 1000) return `${ms} ms`;
  const seconds = Math.round(ms / 1000);
  if (seconds < 90) return `${seconds} s`;
  return `${Math.round(seconds / 60)} min`;
}

/** Bytes as the figure a transcript header needs: "812 B", "1.4 MB". */
export function byteLabel(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
